import { z } from 'zod';

const entityId = z.string().min(1);
const abilityKey = z.enum(['str', 'dex', 'con', 'int', 'wis', 'cha']);
const abilityScores = z.object({
  str: z.number(),
  dex: z.number(),
  con: z.number(),
  int: z.number(),
  wis: z.number(),
  cha: z.number(),
});
const modifiers = z.object({
  damage: z.array(z.string()),
  conditions: z.array(z.string()),
});

export const actionSchema = z.object({
  id: entityId,
  name: z.string().trim().min(1),
  type: z.enum(['attack', 'save', 'utility']),
  section: z.enum(['trait', 'action', 'reaction', 'bonus', 'legendary']).optional(),
  note: z.string().optional(),
  range: z.string().optional(),
  attackBonus: z.number().optional(),
  damages: z.array(z.object({ dice: z.string().min(1), type: z.string().min(1) })),
  saveAbility: abilityKey.optional(),
  saveDC: z.number().optional(),
  onSuccess: z.enum(['half', 'zero']).optional(),
  recharge: z.number().optional(),
  onHitStatus: z
    .enum([
      'prone',
      'restrained',
      'blinded',
      'poisoned',
      'charmed',
      'frightened',
      'deafened',
      'grappled',
      'incapacitated',
      'invisible',
      'paralyzed',
      'petrified',
      'stunned',
      'unconscious',
      'exhaustion',
    ])
    .optional(),
  onHitStatusRounds: z.number().optional(),
  onHitSaveAbility: abilityKey.optional(),
  onHitSaveDC: z.number().optional(),
});

export const monsterSchema = z.object({
  id: entityId,
  name: z.string().trim().min(1),
  avatar: z.string().optional(),
  backgroundImage: z.string().optional(),
  size: z.string().optional(),
  creatureTypeText: z.string().optional(),
  subtypeText: z.string().optional(),
  alignment: z.string().optional(),
  cr: z.number().min(0),
  types: z.array(z.string()),
  ac: z.number().min(0),
  hp: z.object({ average: z.number().min(1), roll: z.string().min(1) }),
  speed: z.record(z.string(), z.number()),
  abilities: abilityScores,
  resistances: modifiers,
  vulnerabilities: modifiers,
  immunities: modifiers,
  actions: z.array(actionSchema),
  features: z.string().optional(),
  isDefault: z.boolean().optional(),
});

export const pcSchema = z.object({
  id: entityId,
  name: z.string().trim().min(1),
  avatar: z.string().optional(),
  backgroundImage: z.string().optional(),
  metaLine: z.string().optional(),
  ac: z.number().min(0),
  hpMax: z.number().min(1),
  hpCurrent: z.number().min(0),
  abilities: abilityScores,
  resistances: modifiers,
  vulnerabilities: modifiers,
  immunities: modifiers,
  actions: z.array(actionSchema),
  features: z.string().optional(),
  isDefault: z.boolean().optional(),
});

export const abilitySchema = z.object({
  id: entityId,
  name: z.string().trim().min(1),
  description: z.string(),
});

export const encounterGroupSchema = z.object({
  id: entityId,
  name: z.string().trim().min(1),
  monsters: z.array(z.object({ monsterId: entityId, count: z.number().int().min(1) })),
});

export const backupSchema = z.object({
  meta: z.object({
    app: z.literal('dnd-assist-react'),
    version: z.literal(1),
    exportedAt: z.string(),
  }),
  monsters: z.array(monsterSchema),
  pcs: z.array(pcSchema),
  actions: z.array(actionSchema),
  abilities: z.array(abilitySchema),
  encounterGroups: z.array(encounterGroupSchema),
});

export type BackupV1 = z.infer<typeof backupSchema>;
